import { RANK_ART } from '@/assets/rewards';
import type { ScaleRank } from '@/lib/api/rewards-hub/models';

/**
 * Visual system of Climb the Ranks, transcribed from the twelve variants of
 * Figma 13013:158366 (six ranks x Default/Claim).
 */

/**
 * The rank's medallion: the backend's art when it sends any, else the drawn
 * badge for that position (Figma 13013:158366 draws six).
 */
export function rankArt(rank: Pick<ScaleRank, 'image' | 'index'>): string {
  return rank.image || RANK_ART[Math.min(6, Math.max(1, rank.index)) - 1];
}

/** Sunken navy pill behind every "x / y" counter. */
export const RANK_PILL_BACKGROUND =
  'linear-gradient(to top, #00132f 0%, #000a2e 52%, #00132f 100%)';
export const RANK_PILL_BORDER = '#002252';
/**
 * A running band fills bright gold; a closed one turns bronze and warms the
 * pill's border to match (Figma 13449:468494, 15.10). Figma draws the fill
 * 35px tall, centred in the pill's 30px and with its gradient spanning 37px,
 * so the stops sit in px past both edges: the pill shows the gradient's middle.
 */
export const RANK_PILL_FILL =
  'linear-gradient(180deg, #ffe6a6 -2.5px, #a15e06 19px, #513101 34.5px)';
export const RANK_PILL_FILL_DONE =
  'linear-gradient(180deg, #6d4e00 -2.5px, #854b00 19px, #281800 34.5px)';
export const RANK_PILL_FILL_BORDER = '#714f00';
/** Warm haze off the fill's diagonal edge; a closed fill's edge runs off the pill. */
export const RANK_PILL_FILL_GLOW = 'drop-shadow(4px 0 13px rgba(255,227,142,0.3))';
export const RANK_PILL_FILL_INNER = 'inset 0 4px 4px rgba(73,43,0,0.25)';
/** The fill's leading edge is cut on the diagonal (Figma Rectangle 240659693). */
export const RANK_PILL_FILL_SKEW = 26;

/**
 * Blurred halo behind a rank that is not locked. The design tints the two
 * lowest rank positions blue and the rest magenta (an index rule, not a name).
 */
export function rankGlow(index: number): string {
  const tint = index <= 2 ? '#74aeff' : '#fa74ff';
  return `radial-gradient(circle, ${tint} 0%, ${tint} 32%, transparent 100%)`;
}

/**
 * The pool a locked rank lights on hover, in the rank's own colour (05.10,
 * Figma 13916:40288 — one glow per rank). Positions past the drawn six take
 * the top one's.
 */
const RANK_HOVER_TINTS = ['#9ac4ff', '#408cff', '#ff66a6', '#ff4052', '#ffd694', '#ff8c38'];

export function rankHoverGlow(index: number): string {
  const tint = RANK_HOVER_TINTS[Math.min(RANK_HOVER_TINTS.length, Math.max(1, index)) - 1];
  return `radial-gradient(circle closest-side, color-mix(in srgb, ${tint} 55%, transparent) 0%, color-mix(in srgb, ${tint} 22%, transparent) 50%, transparent 100%)`;
}

/**
 * How a rank badge is drawn. `plain` is the art on its own — the unlock bar
 * repeats the target's medallion there without halo or corner marker.
 */
export type RankState = 'locked' | 'next' | 'reached' | 'plain';

export interface RankBand {
  /** Progress inside this band — quests for the bottom rank, points above it. */
  current: number;
  /** Band size, or null when there is nothing to measure against. */
  total: number | null;
}

export interface RankBandInput {
  ranks: ScaleRank[];
  /** Position in `ranks`, 0-based. */
  position: number;
  /** Lifetime points. */
  points: number;
  /** The onboarding checklist — what the bottom rank is measured in. */
  onboarding: { done: number; total: number };
  /**
   * `r.i` — the rank the player holds, or null until the onboarding gate opens.
   * It is `max(rank by points, highest rank ever awarded)`: an awarded rank is
   * never taken back, so it can sit above what the points alone would reach.
   */
  heldRankIndex: number | null;
}

/**
 * The counter under a badge. Above the bottom rank it measures the band between
 * the previous rank and this one — "0 / 20", not the lifetime total — so every
 * band reads at its own width.
 *
 * The bottom rank is the exception and it is structural: it carries no points
 * threshold at all, because what opens it is the onboarding checklist. Figma
 * counts it in tasks ("6 / 6") and the contract says the same — its `th` comes
 * back null. Measuring it in points would print a bare lifetime figure under a
 * rank nobody earns with points.
 *
 * Each onboarding step is worth a point, so the first band with a threshold
 * starts at the checklist's size, not at 0: the counters then add up to the
 * player's points, as the profile and the leaderboard print them.
 *
 * Points are counted before the onboarding gate, but no rank above the bottom
 * one can be reached until it is held — so without a rank every band above
 * it reads zero, never "12 / 12" under a locked medallion.
 *
 * Every rank up to the one held reads full, whatever the points say. A rank
 * kept by an award outlives the points that earned it (a ladder hidden, a
 * threshold raised, the catalogue changed), and the bands would otherwise
 * contradict the badges: Master "reached" over an Elite band at "8 / 20"
 * (contract-gaps §10, 24.09). Only the ranks above the one held are counted
 * in points, so the next one may well read empty — honestly, as the points
 * for it are not there.
 */
export function rankBand({
  ranks,
  position,
  points,
  onboarding,
  heldRankIndex,
}: RankBandInput): RankBand {
  const rank = ranks[position];
  const held = heldRankIndex !== null && rank !== undefined && rank.index <= heldRankIndex;
  const threshold = rank?.threshold ?? null;
  if (threshold === null) {
    const tasks = onboarding.total > 0 ? onboarding.total : null;
    return { current: held && tasks !== null ? tasks : onboarding.done, total: tasks };
  }
  // A band starts at the previous rank's threshold. Above the checklist rank
  // there is none, so it starts where the checklist left the player: every
  // step is a point already inside the next threshold, and the bottom badge
  // has shown them — counted from 0, the band would show them twice (15.10).
  const previous = position > 0 ? ranks[position - 1] : undefined;
  const from = previous === undefined ? 0 : (previous.threshold ?? onboarding.total);
  const total = Math.max(1, threshold - from);
  if (held) return { current: total, total };
  if (heldRankIndex === null) return { current: 0, total };
  return { current: Math.min(total, Math.max(0, points - from)), total };
}
